
import React, { useState } from 'react';
import { 
Box, 
TextField, 
FormControl, 
InputLabel, 
Select, 
MenuItem, 
FormControlLabel, 
Checkbox, 
Radio, 
RadioGroup, 
Typography, 
Button, 
FormGroup
} from '@mui/material';

export function FormFieldPreview({ field, onUpdate }) {
    const [searchTerm, setSearchTerm] = useState ('');
    const formatFileSize = (bytes) => {
        if (!bytes) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };
    
    switch (field.type) {
        case 'text':
        case 'email':
        case 'number':
            return (
                <TextField
                    label={field.label}
                    type={field.type}
                    required={field.required}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                />
            );
        
        case 'select':
            return (
                <FormControl fullWidth margin="normal" required={field.required}>
                    <InputLabel>{field.label}</InputLabel>
                    <Select label={field.label}>
                        {field.options?.map((option, index) => (
                            <MenuItem key={index} value={option}>{option}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            );
        
        case 'label':
            return (
                <Typography 
                    variant={field.variant || 'h6'} 
                    sx={{ 
                        mt: 2, 
                        mb: 1,
                        ...(field.sx || {})
                    }}
                >
                    {field.label}
                </Typography>
            );
        
        case 'checkbox':
            return (
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={field.checked || false}
                            onChange={(e) => onUpdate(field.id, { 
                                checked: e.target.checked,
                                groupName: field.groupName 
                            })}
                            name={field.groupName || field.id}
                        />
                    }
                    label={field.label}
                />
            );          case 'radio':
            return (
                <FormControl component="fieldset" margin="normal">
                    <Typography variant="subtitle1">{field.label}</Typography>
                    <RadioGroup
                        value={field.value || ''}
                        onChange={(e) => onUpdate(field.id, { value: e.target.value })}
                        name={field.groupName || field.id}
                    >
                        {field.options?.map((option, index) => (
                            <FormControlLabel 
                                key={index} 
                                value={option} 
                                control={<Radio />} 
                                label={option}
                            />
                        ))}
                    </RadioGroup>
                </FormControl>
            );

        case 'file':
            return (
                <Box margin="normal">
                    <Typography variant="subtitle1">{field.label}</Typography>
                    <Button variant="outlined" component="label">
                        Upload File
                        <input 
                            type="file" 
                            hidden 
                            onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                    onUpdate(field.id, { 
                                        fileName: file.name,
                                        fileSize: file.size,
                                        fileType: file.type
                                    });
                                }
                            }}
                        />
                    </Button>
                    {field.fileName && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            Selected: {field.fileName} ({formatFileSize(field.fileSize)})
                        </Typography>
                    )}
                </Box>
            );
        
        case 'textarea':
            return (
                <TextField
                    label={field.label}
                    multiline
                    rows={4}
                    required={field.required}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                />
            );
        
        case 'date':
            return (
                <TextField
                    label={field.label || "Date"}
                    type="date"
                    value={field.value || ''}
                    onChange={(e) => onUpdate(field.id, { value: e.target.value })}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    InputLabelProps={{
                        shrink: true,
                    }}
                    slotProps={{
                        input: {
                            shrink: true,
                        },
                    }}
                />
            );

		case 'checkbox-group':
			return (
				<Box sx={{ mb: 3 }}>
					<Typography variant="subtitle1">
						{field.label} {field.required && '*'}
					</Typography>
					
					{field.faculties?.map((faculty) => (
						<Box key={faculty.faculty} sx={{ ml: 2, mt: 2 }}>
							<Typography variant="h6" sx={{ fontWeight: 'bold' }}>
								{faculty.faculty}
							</Typography>
							
							{faculty.departments?.map((department) => (
								<Box key={department.id} sx={{ ml: 2, mt: 2 }}>
									<Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
										{department.name}
									</Typography>
									
									<FormGroup sx={{ ml: 2 }}>
										{department.fields?.map((course) => (
											<Box key={course.id} sx={{ mb: 1 }}>
												<FormControlLabel
													control={
														<Checkbox
															checked={course.selected || false}
															onChange={(e) => onUpdate(
																field.id, 
																{ 
																	facultyId: faculty.id,
																	departmentId: department.id,
																	courseId: course.id,
																	selected: e.target.checked 
																}
															)}
														/>
													}
													label={course.course}
												/>
												
												<FormGroup sx={{ ml: 4 }}>
													{course.periods?.map((period) => (
														<FormControlLabel
															key={period.id}
															control={
																<Checkbox
																	checked={period.selected || false}
																	onChange={(e) => onUpdate(
																		field.id,
																		{
																			facultyId: faculty.id,
																			departmentId: department.id,
																			courseId: course.id,
																			periodId: period.id,
																			selected: e.target.checked
																		}
																	)}
																	size="small"
																/>
															}
															label={period.name}
														/>
													))}
												</FormGroup>
											</Box>
										))}
									</FormGroup>
								</Box>
							))}
						</Box>
					))}
				</Box>
			);
        default:
            return (
                <TextField
                    label={field.label}
                    required={field.required}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                />
            );
    }
}